import { Icon } from "../ui";
import type { Project } from "../types";

const STEP_LABELS = [
  "Criada",
  "Descritores",
  "Fontes",
  "Referências",
  "Extração",
  "Visão EN",
  "Revisão EN",
  "Tradução PT",
  "Entregue",
];

export interface ProjectRowProps {
  idx: number;
  project: Project;
  onOpen: () => void;
}

// Backend emits naive UTC ISO (e.g. "2026-05-07T17:23:20.377416" with no
// trailing Z). `new Date(naive)` then parses as LOCAL time on the
// client, which combined with Date.now() (UTC ms) produced negative
// "há -1 dias" relative-time output for fresh rows. Force UTC parsing
// by adding the Z marker when missing.
function parseBackendDate(iso: string): Date {
  const hasTz = /[zZ]$|[+-]\d\d:\d\d$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
}

function relativeAgo(updated: Date): string {
  const d = Math.floor((Date.now() - updated.getTime()) / 86_400_000);
  if (d <= 0) return "hoje";
  if (d === 1) return "ontem";
  return `há ${d} dias`;
}

export function ProjectRow({ idx, project, onOpen }: ProjectRowProps) {
  const updated = parseBackendDate(project.updated_at);
  const ago = relativeAgo(updated);
  const stepLabel = STEP_LABELS[project.current_step] ?? `Passo ${project.current_step}`;
  const hasChemicalId = Boolean(
    project.cas_number || project.molecular_formula || project.iupac_name,
  );

  return (
    <button
      type="button"
      onClick={onOpen}
      className="lift w-full grid grid-cols-[3rem_minmax(0,1fr)] gap-x-4 gap-y-3 py-5 border-b border-[var(--rule-2)] text-left items-start group md:grid-cols-12 md:gap-6 md:py-6 md:items-baseline"
    >
      <div className="col-span-1">
        <span className="step-num text-[28px] tabular text-[var(--ink-4)] group-hover:text-[var(--ink)] transition-colors">
          {String(idx).padStart(2, "0")}
        </span>
      </div>
      <div className="col-span-1 min-w-0 pr-8 md:col-span-4 md:pr-0">
        <div className="display-5 text-[var(--ink)] mb-1">{project.drug_api_name}</div>
        {project.iupac_name && (
          <div className="caption italic line-clamp-1 max-w-md">{project.iupac_name}</div>
        )}
      </div>
      <div className="col-start-2 col-span-1 min-w-0 md:col-start-auto md:col-span-3">
        {hasChemicalId ? (
          <div className="space-y-1 mono text-[12px] text-[var(--ink-2)]">
            <div>{project.cas_number ? `CAS · ${project.cas_number}` : "CAS · não informado"}</div>
            {(project.molecular_formula || project.molecular_weight) && (
              <div className="text-[var(--ink-3)]">
                {[project.molecular_formula, project.molecular_weight].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        ) : (
          <span className="caption italic">Identificação pendente</span>
        )}
      </div>
      <div className="col-start-2 col-span-1 min-w-0 md:col-start-auto md:col-span-2">
        <div className="flex items-center gap-2">
          <span className="mono text-[12px] tabular text-[var(--ink-3)]">
            [{project.current_step}/8]
          </span>
          <span className="text-[13px]">{stepLabel}</span>
        </div>
        <div className="mt-2 h-[2px] bg-[var(--rule-2)] relative w-full max-w-44 md:w-32">
          <div
            className="absolute inset-y-0 left-0 bg-[var(--ink)]"
            style={{ width: `${(project.current_step / 8) * 100}%` }}
          />
        </div>
      </div>
      <div className="col-start-2 col-span-1 caption md:col-start-auto md:col-span-1 md:text-right">{ago}</div>
      <div className="hidden col-span-1 justify-end md:flex">
        <Icon.ArrowUpRight
          size={20}
          strokeWidth={1.5}
          className="text-[var(--ink-3)] group-hover:text-[var(--ink)] transition-colors group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </div>
    </button>
  );
}
